import * as XLSX from 'xlsx';

type SheetInput = {
  name: string;
  rows: Array<Record<string, any>>;
};

type ExportAnalyticsReportInput = {
  fileName: string;
  sheets: SheetInput[];
};

const normalizeValue = (value: any) => {
  if (value === null || value === undefined || value === '') return '-';
  if (Array.isArray(value)) return value.join(', ');
  return value;
};

const toSeriesArray = (value: any) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object' && Array.isArray(value.data)) {
    return [value];
  }
  return [];
};

const sanitizeFilenamePart = (value: string) =>
  value
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, '_');

const sanitizeSheetName = (name: string) => name.replace(/[\\/?*\[\]:]/g, ' ').slice(0, 31);

const addSheet = (workbook: XLSX.WorkBook, name: string, rows: Array<Record<string, any>>) => {
  if (!rows?.length) return;
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), sanitizeSheetName(name));
};

const toSeriesRows = (chart: any) => {
  if (!chart) return [];

  if (Array.isArray(chart?.data) && chart.data.length > 0) {
    const total = chart.data.reduce((sum: number, item: any) => sum + Number(item?.value || 0), 0);
    return chart.data.map((item: any) => ({
      Category: normalizeValue(item?.name),
      Value: normalizeValue(item?.value),
      Percentage: total
        ? `${((Number(item?.value || 0) / total) * 100).toFixed(2)}%`
        : normalizeValue(item?.percentage)
    }));
  }

  const categories =
    chart?.xAxis?.categories ||
    chart?.xAxis?.env ||
    chart?.xAxis?.regions ||
    chart?.yAxis?.categories ||
    (Array.isArray(chart?.xAxis) ? chart.xAxis : []) ||
    [];
  const series =
    toSeriesArray(chart?.series).length > 0
      ? toSeriesArray(chart?.series)
      : toSeriesArray(chart?.request).length > 0
        ? toSeriesArray(chart?.request)
        : toSeriesArray(chart?.cost).length > 0
          ? toSeriesArray(chart?.cost)
          : toSeriesArray(chart?.topRequest).length > 0
            ? toSeriesArray(chart?.topRequest)
            : toSeriesArray(chart?.leftYaxis).length > 0 || toSeriesArray(chart?.rightYaxis).length > 0
              ? [
                  ...toSeriesArray(chart?.leftYaxis).map((item: any) => ({
                    name: chart?.axisName?.leftYaxis || item?.name || 'Left Series',
                    data: item?.data || item
                  })),
                  ...toSeriesArray(chart?.rightYaxis).map((item: any) => ({
                    name: chart?.axisName?.rightYaxis || item?.name || 'Right Series',
                    data: item?.data || item
                  }))
                ]
              : [];

  if (Array.isArray(chart?.yAxis) && chart?.yAxis.length && typeof chart?.yAxis?.[0] !== 'object') {
    return categories.map((category: any, index: number) => ({
      Category: normalizeValue(category),
      Value: normalizeValue(chart?.yAxis?.[index])
    }));
  }

  if (!categories.length || !series.length) return [];

  if (chart?.xAxis?.regions && chart?.xAxis?.providers && series.length) {
    const providers = chart.xAxis.providers;
    const rows: Array<Record<string, any>> = [];
    let cursor = 0;
    for (const region of chart.xAxis.regions) {
      for (const provider of providers) {
        const row: Record<string, any> = {
          Region: normalizeValue(region),
          Provider: normalizeValue(provider)
        };
        series.forEach((s: any) => {
          row[s?.name || 'Value'] = normalizeValue(s?.data?.[cursor]);
        });
        rows.push(row);
        cursor += 1;
      }
    }
    return rows;
  }

  if (chart?.yAxis?.categories && (Array.isArray(chart?.series) || chart?.series?.data)) {
    return categories.map((category: any, index: number) => {
      const row: Record<string, any> = { Category: normalizeValue(category) };
      series.forEach((s: any, seriesIndex: number) => {
        row[s?.name || `Series ${seriesIndex + 1}`] = normalizeValue(s?.data?.[index]);
      });
      return row;
    });
  }

  if (series.length === 1) {
    return categories.map((category: any, index: number) => ({
      Category: normalizeValue(category),
      [series[0]?.name || 'Value']: normalizeValue(series[0]?.data?.[index])
    }));
  }

  return categories.map((category: any, index: number) => {
    const row: Record<string, any> = { Category: normalizeValue(category) };
    series.forEach((s: any) => {
      row[s?.name || 'Value'] = normalizeValue(s?.data?.[index]);
    });
    return row;
  });
};

const healthTimelineRows = (timelineData: any) => {
  const services = timelineData?.services || [];
  return services.flatMap((service: any) => {
    const serviceRows = (service?.blocks || []).map((block: any, index: number) => ({
      Scope: normalizeValue(service?.name),
      Type: 'Service',
      BlockIndex: index + 1,
      Status: normalizeValue(block?.status)
    }));

    const childRows = (service?.children || []).flatMap((child: any) =>
      (child?.blocks || []).map((block: any, index: number) => ({
        Scope: normalizeValue(service?.name),
        Type: normalizeValue(child?.name),
        BlockIndex: index + 1,
        Status: normalizeValue(block?.status)
      }))
    );

    return [...serviceRows, ...childRows];
  });
};

const endpointStatusRows = (rows: Array<Record<string, any>>) =>
  rows.map((row) => ({
    Environment: normalizeValue(row?.environment),
    ModelProvider: normalizeValue(row?.model_provider),
    Model: normalizeValue(row?.model),
    ServedEntityRegion: normalizeValue(row?.served_entity_region),
    StatusCode: normalizeValue(row?.status_code),
    ResponseSummary: normalizeValue(row?.response_summary),
    Risk: normalizeValue(row?.risk),
    LastDataRefreshedAt: normalizeValue(row?.last_data_refreshed_at)
  }));

const filteredRows = (rows: Array<Record<string, any>>, columns: string[]) =>
  rows.map((row) =>
    columns.reduce((acc, column) => {
      acc[column] = normalizeValue(row?.[column]);
      return acc;
    }, {} as Record<string, any>)
  );

const downloadWorkbook = (workbook: XLSX.WorkBook, fileName: string) => {
  const data = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const exportAnalyticsReport = ({ fileName, sheets }: ExportAnalyticsReportInput) => {
  const workbook = XLSX.utils.book_new();
  sheets.forEach((sheet) => addSheet(workbook, sheet.name, sheet.rows));
  downloadWorkbook(workbook, `${sanitizeFilenamePart(fileName)}.xlsx`);
};

export const analyticsSheetBuilders = {
  normalizeValue,
  toSeriesRows,
  healthTimelineRows,
  endpointStatusRows,
  filteredRows
};
